import { LayoutThreeColumns, ViewStacked } from '@styled-icons/bootstrap';
import { MoveHorizontal } from '@styled-icons/boxicons-regular';
import { ColumnElement } from '@typedream/core';
import { setNodes } from '@udecode/plate-core';
import classNames from 'classnames';
import { pick } from 'lodash';
import { memo } from 'react';
import shallow from 'zustand/shallow';
import Tooltip from '../../../components/Tooltip';
import { useTPlateEditorRef } from '../../../plate';
import { getSelectedElementEntry } from '../../../plugins/elementSelection';
import { useDreamEditorStore } from '../../../plugins/store';
import styles from '../SideToolbar.module.css';

// type S =
//   'mobile_direction' |
//   'mobile_horizontal_overflow' |
//   'mobile_reverse_columns' |
//   'direction_vertical';

type ColumnProperties = {
  [K in keyof ColumnElement]?: ColumnElement[K];
};

function ColumnStacking() {
  const editor = useTPlateEditorRef();

  const column = useDreamEditorStore(
    () => pick(getSelectedElementEntry<ColumnElement>(editor)?.[0], [
      'mobile_direction',
      'mobile_reverse_columns',
      'mobile_horizontal_overflow',
    ]),
    shallow,
  );

  const setColumnProperties = (properties: ColumnProperties) => {
    const path = getSelectedElementEntry(editor)?.[1];
    setNodes(editor, properties, { at: path });
  };

  return (
    <div className={styles.section}>
      <div className="is-flex" style={{ gap: 16 }}>
        <div>
          <label className={styles.subLabel} htmlFor="#">
            Stacking
          </label>
          <div className={styles.menuGroup}>
            <button
              className={classNames(styles.menuItem, {
                [styles.active]:
                  !column?.mobile_direction
                  || column?.mobile_direction === 'direction_vertical',
              })}
              type="button"
              onClick={() => {
                setColumnProperties({ mobile_direction: 'direction_vertical' });
              }}
            >
              <Tooltip center="Vertical">
                <div className="p-1">
                  <ViewStacked size={16} />
                </div>
              </Tooltip>
            </button>

            <button
              className={classNames(styles.menuItem, {
                [styles.active]:
                  column?.mobile_direction === 'direction_horizontal',
              })}
              type="button"
              onClick={() => {
                setColumnProperties({
                  mobile_direction: 'direction_horizontal',
                });
              }}
            >
              <Tooltip center="Horizontal">
                <div className="p-1">
                  <ViewStacked
                    size={16}
                    style={{ transform: 'rotate(90deg)' }}
                  />
                </div>
              </Tooltip>
            </button>
          </div>
        </div>
        <div>
          <label className={styles.subLabel} htmlFor="#">
            Ordering
          </label>
          <div className={styles.menuGroup}>
            <button
              className={classNames(styles.menuItem, {
                [styles.active]: !column?.mobile_reverse_columns,
              })}
              type="button"
              onClick={() => {
                setColumnProperties({ mobile_reverse_columns: false });
              }}
            >
              <div className="p-1">Normal</div>
            </button>

            <button
              className={classNames(styles.menuItem, {
                [styles.active]: column?.mobile_reverse_columns,
              })}
              type="button"
              onClick={() => {
                setColumnProperties({ mobile_reverse_columns: true });
              }}
            >
              <div className="p-1">Reverse</div>
            </button>
          </div>
        </div>
      </div>

      {column.mobile_direction === 'direction_horizontal' && (
        <div className="mt-3">
          <label className={styles.subLabel} htmlFor="#">
            Overflow
          </label>
          <div className={styles.menuGroup}>
            <button
              className={classNames(styles.menuItem, {
                [styles.active]:
                  (!column?.align && !column?.mobile_horizontal_overflow)
                  || column?.mobile_horizontal_overflow === 'squeeze',
              })}
              type="button"
              onClick={() => {
                setColumnProperties({ mobile_horizontal_overflow: 'squeeze' });
              }}
            >
              <div className="p-1">
                <LayoutThreeColumns size={16} />
              </div>
              <div className="p-1">Squeeze to Fit</div>
            </button>

            <button
              className={classNames(styles.menuItem, {
                [styles.active]:
                  (column?.align && !column?.mobile_horizontal_overflow)
                  || column?.mobile_horizontal_overflow === 'scroll',
              })}
              type="button"
              onClick={() => {
                setColumnProperties({ mobile_horizontal_overflow: 'scroll' });
              }}
            >
              <div className="p-1">
                <MoveHorizontal size={16} />
              </div>
              <div className="p-1">Scroll</div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(ColumnStacking);
